import React, { useEffect } from "react";
import { Modal, Form, Input, Button } from "antd";
import {
  LockOutlined,
  CloseOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
} from "@ant-design/icons";
import useBodyScrollLock from "../../../../hooks/useBodyScrollLock";
import "./PasswordResetModal.scss";

/**
 * Password Reset Modal Component
 * Modal for resetting user password with input field
 */
const PasswordResetModal = ({
  visible,
  userName,
  userEmail,
  onConfirm,
  onCancel,
  loading = false,
}) => {
  const [form] = Form.useForm();

  // Lock body scroll when modal is open
  useBodyScrollLock(visible);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (visible) {
      form.resetFields();
    }
  }, [visible, form]);

  // Handle form submission
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      onConfirm(values.newPassword);
    } catch (error) {
      console.error("Form validation failed:", error);
    }
  };

  return (
    <Modal
      open={visible}
      onCancel={onCancel}
      footer={null}
      className="password-reset-modal"
      width={440}
      centered={true}
      closeIcon={<CloseOutlined className="close-icon" />}
      maskClosable={false}
      keyboard={true}
      destroyOnClose={true}
      transitionName=""
      maskTransitionName=""
      getContainer={false}
    >
      {/* Modal Content */}
      <div className="modal-content">
        <h3 className="modal-title">Reset Password</h3>
        <p className="modal-subtitle">{userEmail}</p>

        <Form form={form} layout="vertical" className="password-form">
          <Form.Item
            label="New Password"
            name="newPassword"
            rules={[
              { required: true, message: "Please enter a new password" },
              { min: 8, message: "Password must be at least 8 characters" },
              {
                pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
                message:
                  "Password must contain uppercase, lowercase and number",
              },
            ]}
          >
            <Input.Password
              prefix={<LockOutlined className="input-icon" />}
              placeholder="Enter new password"
              iconRender={(visible) =>
                visible ? <EyeOutlined /> : <EyeInvisibleOutlined />
              }
            />
          </Form.Item>

          <Form.Item
            label="Confirm Password"
            name="confirmPassword"
            dependencies={["newPassword"]}
            rules={[
              { required: true, message: "Please confirm the password" },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue("newPassword") === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error("Passwords do not match"));
                },
              }),
            ]}
          >
            <Input.Password
              prefix={<LockOutlined className="input-icon" />}
              placeholder="Confirm new password"
              iconRender={(visible) =>
                visible ? <EyeOutlined /> : <EyeInvisibleOutlined />
              }
            />
          </Form.Item>

          <div className="password-hint">
            Password must be at least 8 characters with uppercase, lowercase and
            numbers
          </div>
        </Form>

        {/* Buttons */}
        <div className="modal-actions">
          <Button onClick={onCancel} className="cancel-btn">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            loading={loading}
            className="submit-btn"
          >
            Reset Password
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default PasswordResetModal;
